import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getApiBaseUrl } from '@/lib/api'
import { getAuthHeaders } from '@/services/api'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { Card } from '@/components/ui/card'
import { X, Trash2, Users, User, Calendar, Settings, Tag as TagIcon, ChevronDown, ChevronUp, Check, Send, MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDistanceToNow } from 'date-fns'
import { useProgramContext } from '@/context/ProgramContext'
import { useTeamContext } from '@/context/TeamContext'
import { canDeleteFeedback } from '@/lib/programPermissions'
import { apiClient } from '@/lib/api'
import { FeatureDetailsTab, FeatureRequest, FeatureSubmitterBlock, Tag } from './FeatureDetailsTab'
import { launchCelebrationConfetti } from '@/utils/confetti'
import { useTranslation } from '@/hooks/useTranslation'
import { useSendReplyShortcut } from '@/hooks/useSendReplyShortcut'
import { AiDraftAssistantPanel } from '@/components/ai/AiDraftAssistantPanel'
import { SendReplyShortcutHint } from '@/components/common/SendReplyShortcutHint'

export interface FeatureRequestUpvote {
	request_id: string
	user_id: string | null
	user_identifier: string | null
	created_at: string
	user_email?: string | null
	user_name?: string | null
	user_avatar?: string | null
}

interface FeatureConversation {
	id: string
	feature_request_id: string
	sender_type: 'support' | 'customer'
	sender_user_id: string | null
	sender_name: string | null
	sender_email: string | null
	message: string
	created_at: string
}

interface FeatureRequestDetailViewProps {
	featureId: string
	onClose: () => void
	onUpdate?: () => void
}

// Utility function to determine text color based on background color luminance
const getTextColorForBackground = (hexColor: string | null): string => {
	if (!hexColor) return '#000000' // Default to black for null colors

	// Remove # if present
	const hex = hexColor.replace('#', '')
	
	// Convert to RGB
	const r = parseInt(hex.substring(0, 2), 16)
	const g = parseInt(hex.substring(2, 2), 16)
	const b = parseInt(hex.substring(4, 2), 16)
	
	// Calculate luminance using the relative luminance formula
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
	
	// Return white for dark colors, black for light colors
	return luminance > 0.5 ? '#000000' : '#ffffff'
}

const getStatusOptions = (t: (key: string) => string) => [
	{ label: t('featureRequests.statusNew'), value: 'New' },
	{ label: t('featureRequests.statusUnderConsideration'), value: 'Under Consideration' },
	{ label: t('featureRequests.statusPlanned'), value: 'Planned' },
	{ label: t('featureRequests.statusInProgress'), value: 'In Progress' },
	{ label: t('featureRequests.statusLaunched'), value: 'Launched' },
	{ label: t('featureRequests.statusDeclined'), value: 'Declined' },
]

const getStatusSelectClassName = (status: string) => {
	switch (status.toLowerCase()) {
		case 'new':
			return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-800'
		case 'under consideration':
			return 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900 dark:text-purple-300 dark:border-purple-800'
		case 'planned':
			return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900 dark:text-amber-300 dark:border-amber-800'
		case 'in progress':
			return 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900 dark:text-orange-300 dark:border-orange-800'
		case 'launched':
			return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900 dark:text-green-300 dark:border-green-800'
		case 'declined':
			return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
		default:
			return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
	}
}

export function FeatureRequestDetailView({
	featureId,
	onClose,
	onUpdate,
}: FeatureRequestDetailViewProps) {
	const { t } = useTranslation()
	const { currentProgram, currentProgramAccessLevel } = useProgramContext()
	const { teamId: contextTeamId } = useTeamContext()
	const canDelete = canDeleteFeedback(currentProgramAccessLevel)
	const [status, setStatus] = useState('')
	const [publicResponse, setPublicResponse] = useState('')
	const [isPublic, setIsPublic] = useState(false)
	const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set())
	const [internalNotes, setInternalNotes] = useState('')
	const [showDeleteDialog, setShowDeleteDialog] = useState(false)
	const [originalStatus, setOriginalStatus] = useState('')
	const [visibleUpvotes, setVisibleUpvotes] = useState(10)
	const [isUpvotesExpanded, setIsUpvotesExpanded] = useState(false)
	const [isHistoryExpanded, setIsHistoryExpanded] = useState(true)
	const [replyMessage, setReplyMessage] = useState('')
	const [isSendingReply, setIsSendingReply] = useState(false)
	const [appliedAiDraft, setAppliedAiDraft] = useState<string | null>(null)
	const [aiSuggestionEventId, setAiSuggestionEventId] = useState<string | null>(null)
	const upvotesRef = useRef<HTMLDivElement>(null)
	const queryClient = useQueryClient()
	const STATUS_OPTIONS = getStatusOptions(t)

	const { data: feature, isLoading } = useQuery({
		queryKey: ['feature', featureId],
		queryFn: async () => {
			return apiClient<
				FeatureRequest & { tags: { id: string; name: string }[] }
			>(`/api/features/protected/${featureId}`)
		},
		enabled: !!featureId,
	})

	const { data: allTags = [] } = useQuery<Tag[]>({
		queryKey: ['tags', currentProgram?.id],
		queryFn: () => apiClient(`/api/tags?programId=${currentProgram?.id}`),
		enabled: !!currentProgram?.id,
	})

	const { data: upvotes, isLoading: upvotesLoading, isError: upvotesError } = useQuery<FeatureRequestUpvote[], Error>({
		queryKey: ['feature_upvotes', featureId],
		queryFn: () =>
			apiClient<FeatureRequestUpvote[]>(
				`/api/features/protected/${featureId}/upvotes`
			),
		enabled: !!featureId,
	})

	interface FeedbackHistory {
		id: string
		feedback_type: string
		feedback_id: string
		field_name: 'status' | 'severity'
		old_value: string | null
		new_value: string
		changed_by_user_id: string | null
		changed_by_team_member_id: string | null
		created_at: string
		changed_by_name?: string
	}

	const { data: history = [], isLoading: isLoadingHistory } = useQuery<FeedbackHistory[]>({
		queryKey: ['feature-history', featureId],
		queryFn: async () => {
			if (!featureId) throw new Error('Feature ID is required')
			const response = await apiClient<{ history: FeedbackHistory[] }>(
				`/api/features/protected/${featureId}/history`
			)
			return response.history || []
		},
		enabled: !!featureId,
	})

	const { data: conversations = [], isLoading: isLoadingConversations } = useQuery<FeatureConversation[]>({
		queryKey: ['feature-conversations', featureId],
		queryFn: async () => {
			if (!featureId) throw new Error('Feature ID is required')
			const response = await fetch(`${getApiBaseUrl()}/api/features/protected/${featureId}/conversations`, {
				headers: await getAuthHeaders(),
			})
			if (!response.ok) throw new Error('Failed to fetch conversations')
			const data = await response.json()
			return data.conversations || []
		},
		enabled: !!featureId,
		refetchInterval: 30000,
	})

	const canSendReply =
		!!replyMessage.trim() &&
		!isSendingReply &&
		currentProgramAccessLevel !== 'read'

	const { onKeyDown: onReplyKeyDown, shortcutLabel } = useSendReplyShortcut(
		handleSendReply,
		{ canSend: canSendReply, isSending: isSendingReply }
	)

	async function handleSendReply() {
		if (!featureId || !replyMessage.trim()) return

		setIsSendingReply(true)
		try {
			const response = await fetch(`${getApiBaseUrl()}/api/features/protected/${featureId}/reply`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...await getAuthHeaders(),
				},
				body: JSON.stringify({
					message: replyMessage,
					aiSuggestionEventId: appliedAiDraft ? aiSuggestionEventId : null,
					aiEdited: appliedAiDraft ? replyMessage !== appliedAiDraft : false,
				}),
			})

			if (!response.ok) {
				const contentType = response.headers.get('content-type')
				let errorMessage = 'Failed to send reply'
				if (contentType && contentType.includes('application/json')) {
					try {
						const error = await response.json()
						errorMessage = error.error || errorMessage
					} catch (e) {
						console.error('Failed to parse error response:', e)
					}
				}
				throw new Error(errorMessage)
			}

			setReplyMessage('')
			setAppliedAiDraft(null)
			setAiSuggestionEventId(null)
			queryClient.invalidateQueries({ queryKey: ['feature-conversations', featureId] })
		} catch (error) {
			console.error('Error sending reply:', error)
			const message = error instanceof Error ? error.message : 'Failed to send reply. Please try again.'
			alert(message)
		} finally {
			setIsSendingReply(false)
		}
	}

	const updateFeatureMutation = useMutation({
		mutationFn: async (updateData: {
			status?: string
			public_response?: string
			is_publicly_visible?: boolean
			tag_ids?: string[]
			internal_notes?: string | null
		}) => {
			return apiClient(`/api/features/protected/${featureId}`, {
				method: 'PUT',
				data: updateData,
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['feature', featureId] })
			queryClient.invalidateQueries({ queryKey: ['features'] })
			queryClient.invalidateQueries({ queryKey: ['publicRoadmap'] })
			queryClient.invalidateQueries({ queryKey: ['feature-history', featureId] })
			onUpdate?.()
		},
	})

	const deleteFeatureMutation = useMutation({
		mutationFn: async () => {
			return apiClient(`/api/features/protected/${featureId}`, {
				method: 'DELETE',
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['features'] })
			queryClient.invalidateQueries({ queryKey: ['roadmapDataWithFeatures'] })
			queryClient.invalidateQueries({ queryKey: ['publicRoadmap'] })
			onUpdate?.()
			onClose()
		},
	})

	useEffect(() => {
		if (feature) {
			setStatus(feature.status)
			if (!originalStatus) {
				setOriginalStatus(feature.status)
			}
			setPublicResponse(feature.public_response || '')
			setIsPublic(feature.is_publicly_visible)
			setSelectedTagIds(new Set(feature.tags?.map((t) => t.id) || []))
			setInternalNotes(feature.internal_notes || '')
		}
	}, [feature, originalStatus])

	useEffect(() => {
		if (feature && (feature.upvote_count || 0) > 0) {
			setIsUpvotesExpanded(true)
		}
	}, [feature?.upvote_count])

	const handleTagToggle = (tagId: string) => {
		setSelectedTagIds((prev) => {
			const newSet = new Set(prev)
			if (newSet.has(tagId)) {
				newSet.delete(tagId)
			} else {
				newSet.add(tagId)
			}
			return newSet
		})
	}

	const handleStatusChange = (newStatus: string) => {
		setStatus(newStatus)

		if (newStatus === 'Launched' && originalStatus !== 'Launched') {
			try {
				launchCelebrationConfetti()
			} catch (error) {
				console.error('Error launching confetti for status change:', error)
			}
		}

		if (currentProgramAccessLevel !== 'read') {
			updateFeatureMutation.mutate(
				{ status: newStatus },
				{
					onSuccess: () => {
						setOriginalStatus(newStatus)
						onUpdate?.()
					},
					onError: (error) => {
						console.error('Status update error:', error)
						setStatus(feature?.status ?? '')
					},
				}
			)
		}
	}

	const handleSave = () => {
		updateFeatureMutation.mutate(
			{
				status,
				public_response: publicResponse,
				is_publicly_visible: isPublic,
				tag_ids: Array.from(selectedTagIds),
				internal_notes: internalNotes,
			},
			{
				onSuccess: () => {
					onUpdate?.()
					onClose()
				},
				onError: (error) => {
					console.error('Save error:', error)
				},
			}
		)
	}

	const handleDelete = () => {
		setShowDeleteDialog(true)
	}

	const confirmDelete = () => {
		deleteFeatureMutation.mutate()
		setShowDeleteDialog(false)
	}

	const handleClose = () => {
		onClose()
	}

	const scrollToUpvotes = () => {
		setIsUpvotesExpanded(true)
		upvotesRef.current?.scrollIntoView({ behavior: 'smooth' })
	}

	const showMoreUpvotes = () => {
		setVisibleUpvotes(prev => prev + 10)
	}

	if (isLoading || !feature) {
		return (
			<Dialog
				defaultOpen
				onOpenChange={(open) => {
					if (!open) {
						onClose()
					}
				}}>
				<DialogContent className="sm:max-w-xl md:max-w-2xl max-h-[90vh]">
					<DialogHeader className="space-y-2">
						<Skeleton className="h-6 w-24 rounded-full" />
						<Skeleton className="h-7 w-3/4" />
						<Skeleton className="h-4 w-1/2" />
					</DialogHeader>
					<div className="mt-6 space-y-4">
						<Skeleton className="h-10 w-full" />
						<Skeleton className="h-48 w-full" />
					</div>
				</DialogContent>
			</Dialog>
		)
	}

	const upvotesCount = feature.upvote_count || 0
	const displayedUpvotes = upvotes?.slice(0, visibleUpvotes) || []
	const hasMoreUpvotes = upvotes && upvotes.length > visibleUpvotes

	return (
		<>
			<Dialog
				defaultOpen
				onOpenChange={(open) => {
					if (!open) {
						handleClose()
					}
				}}>
				<DialogContent className="sm:max-w-2xl md:max-w-4xl lg:max-w-6xl overflow-y-auto max-h-[90vh]">
					<DialogHeader className="space-y-2 pr-8">
						<DialogTitle className="text-xl font-semibold truncate text-left">
							{feature.title || t('featureRequestDetailView.untitledFeature')}
						</DialogTitle>
						<div className="flex flex-wrap items-center gap-3 pt-2">
							<div className="flex items-center gap-2">
								<span className="text-xs font-medium text-muted-foreground">
									{t('featureDetails.status')}
								</span>
								<Select
									value={status}
									onValueChange={handleStatusChange}
									disabled={currentProgramAccessLevel === 'read'}
								>
									<SelectTrigger
										className={cn(
											'w-40 h-7 text-xs',
											getStatusSelectClassName(status || feature.status)
										)}
									>
										<SelectValue
											placeholder={t('featureDetails.selectStatus')}
										/>
									</SelectTrigger>
									<SelectContent>
										{STATUS_OPTIONS.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="flex items-center gap-2">
								<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
									<TagIcon className="h-3 w-3" />
									{t('featureDetails.tags')}
								</span>
								<Popover>
									<PopoverTrigger asChild>
										<Button
											variant="outline"
											size="sm"
											disabled={currentProgramAccessLevel === 'read'}
											className="h-7 px-3 text-xs flex items-center gap-1"
										>
											<span>
												{selectedTagIds.size > 0
													? t('featureDetails.tagsSelected', {
															count: selectedTagIds.size,
													  })
													: t('featureDetails.selectTags')}
											</span>
											<ChevronDown className="h-3 w-3" />
										</Button>
									</PopoverTrigger>
									{currentProgramAccessLevel !== 'read' && (
										<PopoverContent className="w-56 p-0">
											<Command>
												<CommandInput
													placeholder={t('featureDetails.filterTags')}
												/>
												<CommandList>
													<CommandEmpty>
														{t('featureDetails.noTagsFound')}
													</CommandEmpty>
													<CommandGroup>
														{allTags.map((tag) => (
															<CommandItem
																key={tag.id}
																onSelect={() => handleTagToggle(tag.id)}
															>
																<div
																	className={cn(
																		'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
																		selectedTagIds.has(tag.id)
																			? 'bg-primary text-primary-foreground'
																			: 'opacity-50 [&_svg]:invisible'
																	)}
																>
																	<Check className="h-3 w-3" />
																</div>
																<span>{tag.name}</span>
															</CommandItem>
														))}
													</CommandGroup>
												</CommandList>
											</Command>
										</PopoverContent>
									)}
								</Popover>
							</div>

							{upvotesCount > 0 && (
								<button
									onClick={scrollToUpvotes}
									className="ml-auto text-xs text-muted-foreground hover:text-primary transition-colors cursor-pointer flex items-center gap-1"
								>
									<Users className="h-4 w-4" />
									{upvotesCount}{' '}
									{upvotesCount === 1
										? t('featureRequestDetailView.upvote')
										: t('featureRequestDetailView.upvotes')}
								</button>
							)}
						</div>
					</DialogHeader>

					<Separator className="my-2" />

					<div className="space-y-6">
						<div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
							<div className="lg:col-span-8 space-y-6">
								<FeatureDetailsTab
									feature={feature}
									internalNotes={internalNotes}
									onInternalNotesChange={setInternalNotes}
									isReadOnly={currentProgramAccessLevel === 'read'}
								/>

								<div ref={upvotesRef} className="space-y-3">
									<button
										type="button"
										onClick={() => setIsUpvotesExpanded(!isUpvotesExpanded)}
										className="text-sm font-medium text-muted-foreground flex items-center justify-between w-full hover:text-foreground transition-colors"
									>
										<div className="flex items-center gap-2">
											<div className="h-4 w-4 flex items-center justify-center bg-primary/20 dark:bg-primary/30 rounded-full">
												<Users className="h-2.5 w-2.5 text-primary" />
											</div>
											{t('featureRequestDetailView.upvotes')}
											{upvotesCount > 0 && (
												<span className="text-xs font-normal text-muted-foreground">
													({upvotesCount})
												</span>
											)}
										</div>
										{isUpvotesExpanded ? (
											<ChevronUp className="h-4 w-4" />
										) : (
											<ChevronDown className="h-4 w-4" />
										)}
									</button>
									{isUpvotesExpanded && (
									<div className="bg-muted/50 p-4 rounded-lg space-y-3">
									{upvotesLoading ? (
										<div className="space-y-3">
											{[...Array(3)].map((_, i) => (
												<div key={i} className="flex items-center gap-3 p-2">
													<Skeleton className="h-8 w-8 rounded-full" />
													<div className="flex flex-col gap-1.5 flex-1">
														<Skeleton className="h-4 w-24" />
														<Skeleton className="h-3 w-16" />
													</div>
												</div>
											))}
										</div>
									) : upvotesError ? (
										<div className="text-center py-8 text-destructive">
											<User className="h-10 w-10 mx-auto mb-2 opacity-50" />
											<p className="font-medium">
												{t('featureRequestDetailView.errorLoadingUpvoters')}
											</p>
											<p className="text-sm text-muted-foreground mt-1">
												{t('featureRequestDetailView.pleaseTryAgainLater')}
											</p>
										</div>
									) : !upvotes || upvotes.length === 0 ? (
										<div className="text-center py-8">
											<User className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
											<h4 className="text-sm font-medium text-foreground">
												{t('featureRequestDetailView.noUpvotesYet')}
											</h4>
											<p className="text-xs text-muted-foreground mt-1">
												{t('featureRequestDetailView.featureHasntReceivedUpvotes')}
											</p>
										</div>
									) : (
										<>
											<div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
												<span>
													{upvotesCount}{' '}
													{upvotesCount === 1
														? t('featureRequestDetailView.user')
														: t('featureRequestDetailView.users')}{' '}
													{t('featureRequestDetailView.upvotedThisFeature')}
												</span>
												<Badge variant="secondary" className="text-xs font-medium">
													{upvotesCount}
												</Badge>
											</div>
											<Separator className="my-3" />
											<div className="space-y-3">
												{displayedUpvotes.map((upvote, index) => (
													<div
														key={upvote.user_id || upvote.user_identifier || index}
														className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors"
													>
														<Avatar className="h-8 w-8 ring-2 ring-primary/10">
															{upvote.user_avatar && (
																<AvatarImage src={upvote.user_avatar} />
															)}
															<AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
																{(
																	upvote.user_name ||
																	upvote.user_email ||
																	'A'
																)[0].toUpperCase()}
															</AvatarFallback>
														</Avatar>
														<div className="flex-1 min-w-0">
															<p className="text-sm font-medium text-foreground truncate">
																{upvote.user_name ||
																	upvote.user_email ||
																	t('featureRequestDetailView.anonymousUser')}
															</p>
															<p className="text-xs text-muted-foreground">
																{formatDistanceToNow(
																	new Date(upvote.created_at),
																	{
																		addSuffix: true,
																	}
																)}
															</p>
														</div>
													</div>
												))}
											</div>
											{hasMoreUpvotes && (
												<div className="flex justify-center mt-4">
													<Button
														variant="outline"
														size="sm"
														onClick={showMoreUpvotes}
														className="text-xs"
													>
														{t('featureRequestDetailView.seeMoreUpvotes')}
													</Button>
												</div>
											)}
										</>
									)}
								</div>
									)}
							</div>

							{history.length > 0 && (
								<div className="space-y-3">
									<button
										type="button"
										onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
										className="text-sm font-medium text-muted-foreground flex items-center justify-between w-full hover:text-foreground transition-colors"
									>
										<div className="flex items-center gap-2">
											<div className="h-4 w-4 flex items-center justify-center bg-primary/20 dark:bg-primary/30 rounded-full">
												<Calendar className="h-2.5 w-2.5 text-primary" />
											</div>
											{t('bugReportDetailView.history', 'History')}
										</div>
										{isHistoryExpanded ? (
											<ChevronUp className="h-4 w-4" />
										) : (
											<ChevronDown className="h-4 w-4" />
										)}
									</button>
									{isHistoryExpanded && (
									<div className="bg-muted/50 p-4 rounded-lg space-y-3">
										{isLoadingHistory ? (
											<div className="flex justify-center py-4">
												<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
											</div>
										) : (
											history.map((item) => (
												<div
													key={item.id}
													className="flex items-start gap-3 pb-3 border-b last:border-b-0 last:pb-0"
												>
													<div className="h-8 w-8 flex items-center justify-center flex-shrink-0">
														<Settings className="h-4 w-4 text-primary" />
													</div>
													<div className="flex-1 min-w-0">
														<div className="flex items-center justify-between gap-2">
															<span className="text-sm font-medium">
																{item.field_name === 'status'
																	? t(
																			'bugReportDetailView.statusChanged',
																			'Status changed'
																	  )
																	: t(
																			'bugReportDetailView.severityChanged',
																			'Severity changed'
																	  )}
															</span>
															<div className="text-right flex-shrink-0">
																<div className="text-xs text-muted-foreground">
																	{formatDistanceToNow(
																		new Date(item.created_at),
																		{ addSuffix: true }
																	)}
																</div>
																{item.changed_by_name && (
																	<div className="text-xs text-muted-foreground">
																		{t(
																			'bugReportDetailView.changedBy',
																			'Changed by'
																		)}{' '}
																		{item.changed_by_name}
																	</div>
																)}
															</div>
														</div>
														<div className="text-sm mt-1">
															<span className="text-muted-foreground">
																{item.old_value ||
																	t('bugReportDetailView.none', 'None')}
															</span>
															<span className="mx-2">→</span>
															<span className="font-medium">
																{item.new_value}
															</span>
														</div>
													</div>
												</div>
											))
										)}
									</div>
									)}
								</div>
							)}

							{feature.submitter_email && (
								<div className="space-y-3">
									<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
										<div className="h-4 w-4 flex items-center justify-center bg-primary/20 dark:bg-primary/30 rounded-full">
											<MessageCircle className="h-2.5 w-2.5 text-primary" />
										</div>
										{t('bugReportDetailView.conversation', 'Conversation')}
									</h3>

									<div className="space-y-3">
										{isLoadingConversations ? (
											<div className="flex justify-center py-4">
												<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
											</div>
										) : conversations.length > 0 ? (
											<div className="space-y-3">
												{conversations.map((conv) => (
													<Card key={conv.id} className={`p-4 ${conv.sender_type === 'support' ? 'bg-blue-50 dark:bg-blue-950/20' : 'bg-muted/50'}`}>
														<div className="flex items-start gap-3">
															<div className={`h-8 w-8 rounded-full flex items-center justify-center ${conv.sender_type === 'support' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-gray-200 dark:bg-gray-700'}`}>
																<User className={`h-4 w-4 ${conv.sender_type === 'support' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`} />
															</div>
															<div className="flex-1 space-y-1">
																<div className="flex items-center justify-between">
																	<span className="text-sm font-medium">
																		{conv.sender_name || (conv.sender_type === 'support' ? 'Support' : 'Customer')}
																	</span>
																	<span className="text-xs text-muted-foreground">
																		{formatDistanceToNow(new Date(conv.created_at), { addSuffix: true })}
																	</span>
																</div>
																<p className="text-sm whitespace-pre-wrap">{conv.message}</p>
															</div>
														</div>
													</Card>
												))}
											</div>
										) : (
											<div className="text-center py-6 text-sm text-muted-foreground">
												{t('bugReportDetailView.noConversation', 'No messages yet. Send a reply to start the conversation.')}
											</div>
										)}
									</div>

									{currentProgramAccessLevel !== 'read' && (
										<div className="space-y-3">
											<AiDraftAssistantPanel
												teamId={contextTeamId}
												programId={currentProgram?.id}
												feedbackType="features"
												feedbackId={featureId}
												canWrite={currentProgramAccessLevel !== 'read'}
												onApplyDraft={(draft, eventId) => {
													setReplyMessage(draft)
													setAppliedAiDraft(draft)
													setAiSuggestionEventId(eventId)
												}}
												onSendReply={async (message, eventId, wasEdited) => {
													if (!featureId || !message.trim()) return
													setIsSendingReply(true)
													try {
														const response = await fetch(`${getApiBaseUrl()}/api/features/protected/${featureId}/reply`, {
															method: 'POST',
															headers: {
																'Content-Type': 'application/json',
																...await getAuthHeaders(),
															},
															body: JSON.stringify({
																message,
																aiSuggestionEventId: eventId,
																aiEdited: wasEdited,
															}),
														})
														if (!response.ok) {
															const contentType = response.headers.get('content-type')
															let errorMessage = 'Failed to send reply'
															if (contentType && contentType.includes('application/json')) {
																try {
																	const error = await response.json()
																	errorMessage = error.error || errorMessage
																} catch (e) {
																	console.error('Failed to parse error response:', e)
																}
															}
															throw new Error(errorMessage)
														}
														setReplyMessage('')
														setAppliedAiDraft(null)
														setAiSuggestionEventId(null)
														queryClient.invalidateQueries({ queryKey: ['feature-conversations', featureId] })
													} catch (error) {
														console.error('Error sending reply:', error)
														const msg = error instanceof Error ? error.message : 'Failed to send reply. Please try again.'
														alert(msg)
														throw error
													} finally {
														setIsSendingReply(false)
													}
												}}
												isSendingReply={isSendingReply}
												onSuggestionCleared={() => {
													setAppliedAiDraft(null)
													setAiSuggestionEventId(null)
												}}
												resetToken={featureId}
												showKnowledgeBaseLink
											/>
											<Textarea
												placeholder={t('bugReportDetailView.typeReply', 'Type your reply...')}
												value={replyMessage}
												onChange={(e) => setReplyMessage(e.target.value)}
												onKeyDown={onReplyKeyDown}
												className="min-h-[100px]"
												disabled={isSendingReply}
											/>
											<div className="flex justify-end">
												<Button
													onClick={handleSendReply}
													disabled={!replyMessage.trim() || isSendingReply}
													size="sm"
												>
													<Send className="h-4 w-4 mr-2" />
													{isSendingReply ? t('bugReportDetailView.sending', 'Sending...') : t('bugReportDetailView.sendReply', 'Send Reply')}
													{!isSendingReply && <SendReplyShortcutHint label={shortcutLabel} />}
												</Button>
											</div>
										</div>
									)}
								</div>
							)}
						</div>

						<div className="lg:col-span-4 lg:border-l lg:border-border lg:pl-6 space-y-6">

							<FeatureSubmitterBlock feature={feature} />
						</div>
					</div>
					</div>

					<Separator className="my-2" />
					<div className="flex justify-between items-center pt-2">
						{!canDelete ? (
							<div className="text-sm text-muted-foreground">
								{t('featureRequestDetailView.readOnlyAccess')}
							</div>
						) : (
							<Button
								variant="destructive"
								size="sm"
								onClick={handleDelete}
								disabled={deleteFeatureMutation.isPending}
								className="flex items-center gap-2">
								<Trash2 className="h-4 w-4" />
								<span>{t('featureRequestDetailView.delete')}</span>
							</Button>
						)}

						<div className="flex gap-3">
							<Button variant="outline" onClick={handleClose}>
								{currentProgramAccessLevel === 'read' ? t('featureRequestDetailView.close') : t('featureRequestDetailView.cancel')}
							</Button>
							{currentProgramAccessLevel !== 'read' && (
								<Button
									onClick={handleSave}
									disabled={updateFeatureMutation.isPending}
									className="bg-primary text-primary-foreground hover:bg-primary/90 min-w-[120px]">
									{updateFeatureMutation.isPending ? t('featureRequestDetailView.saving') : t('featureRequestDetailView.saveChanges')}
								</Button>
							)}
						</div>
					</div>
				</DialogContent>
			</Dialog>

			<AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('featureRequestDetailView.areYouSure')}</AlertDialogTitle>
						<AlertDialogDescription>
							{t('featureRequestDetailView.deleteFeatureConfirmation')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('featureRequestDetailView.cancel')}</AlertDialogCancel>
						<AlertDialogAction
							onClick={confirmDelete}
							disabled={deleteFeatureMutation.isPending}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
							{deleteFeatureMutation.isPending ? t('featureRequestDetailView.deleting') : t('featureRequestDetailView.delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
